import { useState, useEffect, useCallback } from 'react';
import { WeatherData, LocationData, WeatherError, WeatherErrorType } from '../../api/weather/types';
import { WeatherService } from '../../api/weather/weatherService';
import { FallbackWeatherData, WeatherErrorHandler } from '../../api/weather/utils/errorHandler';

interface UseWeatherState {
  weather: WeatherData | FallbackWeatherData | null;
  isLoading: boolean;
  error: string | null;
  isFallback: boolean;
  fallbackReason?: string;
  errorSeverity?: 'low' | 'medium' | 'high';
}

interface UseWeatherReturn extends UseWeatherState {
  refetch: () => Promise<void>;
  clearCache: () => void;
  hasRecoverableError: boolean;
}

interface UseWeatherOptions {
  location?: LocationData | null;
  enabled?: boolean;
}

export const useWeather = (options: UseWeatherOptions = {}): UseWeatherReturn => {
  const { location, enabled = true } = options;
  
  const [state, setState] = useState<UseWeatherState>({
    weather: null,
    isLoading: false,
    error: null,
    isFallback: false,
  });

  const fetchWeather = useCallback(async () => {
    if (!location || !enabled) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: null,
        isFallback: false 
      }));
      return;
    }

    setState(prev => ({ 
      ...prev, 
      isLoading: true, 
      error: null,
      isFallback: false 
    }));

    try {
      const weather = await WeatherService.getWeatherData(location);
      
      // Check if this is fallback data
      const isFallback = 'isFallback' in weather && weather.isFallback;
      const fallbackReason = isFallback ? (weather as FallbackWeatherData).fallbackReason : undefined;
      
      setState({
        weather,
        isLoading: false,
        error: null,
        isFallback,
        fallbackReason,
        errorSeverity: undefined
      });
    } catch (error) {
      const weatherError = error instanceof WeatherError ? error : new WeatherError(
        'weather_api_failed' as WeatherErrorType,
        'Failed to fetch weather data',
        error as Error
      );
      
      const errorSeverity = WeatherErrorHandler.getErrorSeverity(weatherError);
      const userMessage = WeatherErrorHandler.createUserFriendlyMessage(weatherError);
      
      setState({
        weather: null,
        isLoading: false,
        error: userMessage,
        isFallback: false,
        errorSeverity
      });
    }
  }, [location, enabled]);

  const refetch = useCallback(async () => {
    await fetchWeather();
  }, [fetchWeather]);

  const clearCache = useCallback(() => {
    WeatherService.clearWeatherCache();
  }, []);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  const hasRecoverableError = state.error ? 
    WeatherErrorHandler.isRecoverableError(new Error(state.error)) : 
    false;

  return {
    ...state,
    refetch,
    clearCache,
    hasRecoverableError,
  };
};